import React, { ReactNode, useCallback, useMemo, useState } from "react";
import { connect } from "react-redux";

import { faPalette } from "@fortawesome/pro-solid-svg-icons/faPalette";
import { faPen } from "@fortawesome/pro-solid-svg-icons/faPen";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Tooltip from "antd/es/tooltip";
import { useIntl } from "react-intl";

import { EntityTypesSupportedByMapsPage } from "@mapmycustomers/shared/types/entity";
import PinLegend from "@mapmycustomers/shared/types/map/PinLegend";
import User from "@mapmycustomers/shared/types/User";
import { Button } from "@mapmycustomers/ui";

import useMapView from "@app/component/view/utils/useMapView";
import { showUpdatePinLegendDrawer } from "@app/scene/map/store/pinLegends/actions";
import { getCurrentUserId, isCurrentUserOwner, isCurrentUserReadOnly } from "@app/store/iam";
import { getColorLegends } from "@app/store/pinLegends/selectors";
import { RootState } from "@app/store/rootReducer";
import useAnalytics from "@app/util/contexts/useAnalytics";
import { getEntityTypeAnalyticsName } from "@app/util/ui";

import canEditPinLegend from "../utils/canEditPinLegend";
import usePinLegendOptions from "../utils/usePinLegendOptions";

import PinLegendSelectField from "./PinLegendSelectField";

const analyticsTypeName: Record<string, string> = {
  color: "Color",
  groups: "Groups",
  noContactDaysOut: "Last Contact",
  outOfCadence: "Cadence Status",
  stage: "Stage Color",
  territories: "Territories",
};

interface OwnProps {
  className?: string;
  entityType: EntityTypesSupportedByMapsPage;
  label?: ReactNode;
  onCreateCustom?: () => void;
}

interface Props extends OwnProps {
  currentUserId: User["id"];
  isOwner?: boolean;
  isReadOnlyMember: boolean;
  onEditPinLegend?: (pinLegend: PinLegend) => void;
  pinLegends: PinLegend[];
}

const PinColorSelectField: React.FC<Props> = ({
  className,
  currentUserId,
  entityType,
  isOwner,
  isReadOnlyMember,
  label,
  onCreateCustom,
  onEditPinLegend,
  pinLegends,
}) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const [isOpen, setOpen] = useState<boolean>(false);
  const handleShowEditor = useCallback(
    (legend: PinLegend) => {
      onEditPinLegend?.(legend);
      setOpen(false);
    },
    [onEditPinLegend]
  );

  const options = usePinLegendOptions(pinLegends, handleShowEditor);

  const {
    updateViewStateAndFetch,
    viewState: { colorKey },
  } = useMapView();

  const handleChange = useCallback(
    (pinLegendId?: PinLegend["id"]) => {
      updateViewStateAndFetch({ request: { colorKey: { [entityType]: pinLegendId } } });
      const pinLegend = pinLegends.find(({ id }) => id === pinLegendId);
      analytics.clicked(["Color key"], {
        entityType: getEntityTypeAnalyticsName(entityType, false, false),
        type: pinLegend?.fieldName ? analyticsTypeName[pinLegend.fieldName] ?? "custom" : "unknown",
      });
    },
    [analytics, entityType, pinLegends, updateViewStateAndFetch]
  );

  const currentPinLegend = useMemo(
    () => pinLegends.find(({ id }) => id === colorKey?.[entityType]),
    // we have both colorKey and entityType in a form of expression here, that's sufficient
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pinLegends, colorKey?.[entityType]]
  );

  const canEdit = currentPinLegend
    ? canEditPinLegend(currentPinLegend, currentUserId, isOwner) && !isReadOnlyMember
    : false;

  const handleEdit = useCallback(() => {
    if (currentPinLegend) {
      onEditPinLegend?.(currentPinLegend);
    }
  }, [currentPinLegend, onEditPinLegend]);

  return (
    <div className={className}>
      <PinLegendSelectField
        icon={faPalette}
        label={label}
        onChange={handleChange}
        onCreateCustom={onCreateCustom}
        onDropdownVisibleChange={setOpen}
        open={isOpen}
        options={options}
        value={colorKey?.[entityType]}
      />
      {(canEdit || isOwner) && (
        <Tooltip
          title={intl.formatMessage({
            id: "map.toolbar.popovers.pinSettings.entitySettings.colorSelect.edit.tooltip",
            defaultMessage:
              "Legends available by default cannot be edited and are available to everyone",
            description: "Tooltip of Edit button next to Color Select in Pin Settings popover",
          })}
          trigger={currentPinLegend?.default ? ["hover"] : []}
        >
          <Button disabled={!canEdit} onClick={handleEdit} size="large">
            {intl.formatMessage(
              {
                id: "map.toolbar.popovers.pinSettings.entitySettings.colorSelect.edit",
                defaultMessage: "{icon} Edit",
                description: "Title of Edit button next to Color Select in Pin Settings popover",
              },
              {
                icon: <FontAwesomeIcon icon={faPen} />,
              }
            )}
          </Button>
        </Tooltip>
      )}
    </div>
  );
};

const mapStateToProps = (state: RootState, { entityType }: OwnProps) => ({
  currentUserId: getCurrentUserId(state)!,
  isOwner: isCurrentUserOwner(state),
  isReadOnlyMember: isCurrentUserReadOnly(state),
  pinLegends: getColorLegends(state)(entityType),
});

const mapDispatchToProps = {
  onEditPinLegend: showUpdatePinLegendDrawer,
};

export default connect(mapStateToProps, mapDispatchToProps)(PinColorSelectField);
